import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

// Types adapted for Chasseur de Pépites
interface PepiteOpportunity {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    score: number;
    signal_type: string;
    entry_price: number;
    target_price: number | string;
    stop_loss: number;
    position_size: number;
    position_value: number;
    cci?: number;
    z_score?: number;
    obv_trend?: string;
    volume_spike?: number;
    justification: string;
    // Trend data
    trend_days?: number;
    trend_status?: string;
    current_pression?: number;
    current_ve?: number;
}

interface PortfolioSummary {
    cash_available: number;
    cash_blocked_j2: number;
    total_portfolio_value: number;
    open_swing_positions: number;
    max_position_value: number;
    risk_per_trade: number;
}

interface EligibleStock {
    symbole: string;
    nom_societe: string;
    secteur?: string;
    cours_actuel: number;
    score: number;
    cci: number;
    z_score: number;
    obv_trend: string;
    volume_ratio: number;
    is_liquid: boolean;
    is_opportunity: boolean;
    is_owned: boolean;
    analysis: string;
    // Trend data
    trend_start_date?: string;
    trend_days?: number;
    trend_status?: 'active' | 'warning' | 'ended' | 'neutral' | 'unknown';
    trend_message?: string;
    current_pression?: number;
    current_ve?: number;
}

interface PepitesData {
    generated_at: string;
    portfolio_summary: PortfolioSummary;
    opportunities: PepiteOpportunity[];
    sell_signals: any[]; // Less relevant for pure trend following initially
    eligible_universe: EligibleStock[];
    market_sentiment: string;
}

export const usePepites = () => {
    const { token } = useAuth();
    const [data, setData] = useState<PepitesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/portfolio/swing-trader/pepites`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du chargement');
            }

            const jsonData = await response.json();
            setData(jsonData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, refetch: fetchData };
};
